// REACT IMPORTS
import React, { Fragment } from "react";

// COMPONENT IMPORTS
import { Dialog, Transition } from "@headlessui/react";
import { CloseIcon } from "icons/close/CloseIcon";
import Button from "components/form/Button";

// PROPS TYPES
interface CustomModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  title?: string;
  className?: string;
  onCloseClick: (e?: any) => void;
  onDoneClick?: (e?: any) => void;
  children: any;
  doneText?: string;
  cancleText?: string;
  signModal?: boolean;
  otpModal?: boolean;
  isSessionModal?: boolean;
  isSubscribeModal?: boolean;
  hasDoneButton?: boolean;
  isPreferences?: boolean;
  "data-testid"?: string;
  "aria-label"?: string;
}

/* ------------------------------------- MODAL ------------------------------------- */

const CustomModal = (props: CustomModalProps) => {
  const {
    isOpen = false,
    title,
    children,
    className = "",
    isLoading,
    doneText = "Confirm",
    cancleText = "Cancel",
    onCloseClick,
    onDoneClick,
    signModal,
    otpModal = false,
    isSessionModal = false,
    isSubscribeModal = false,
    hasDoneButton = true,
    isPreferences = false,
    ...modalProps
  } = props;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[999] bg-black bg-opacity-70"
        onClose={onCloseClick}
        data-testid={modalProps["data-testid"]}
        aria-label={modalProps["aria-label"] || title}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 ${
              isSessionModal ? "bg-[#00000066] backdrop-blur-sm" : "bg-black/25"
            }`}
          />
        </Transition.Child>

        <div className="fixed inset-0 lg:overflow-y-auto scrollbar-hide lg:scrollbar-auto">
          <div
            className={`flex min-h-full items-center ${
              isSubscribeModal
                ? "justify-end lg:p-10 p-5"
                : "justify-center p-4 pt-[60px]"
            }  lg:pt-4 text-center`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`relative transform ${isPreferences ? "w-full max-h-[calc(var(--shell-vh)*0.98)] overflow-y-auto" : "overflow-hidden"} rounded-2xl bg-white p-5 lg:p-[30px] text-left align-middle shadow-xl transition-all ${className}`}
              >
                {title ? (
                  <Dialog.Title
                    as="h3"
                    className="lg:!text-2xl !text-xl !font-medium leading-normal text-iconObscure !w-[90%] break-words"
                  >
                    {title}
                  </Dialog.Title>
                ) : null}
                <button
                  type="button"
                  className={`${
                    isSessionModal ? "hidden" : ""
                  } absolute top-5 right-5 cursor-pointer `}
                  aria-label="Close modal"
                  data-testid={
                    modalProps["data-testid"]
                      ? `${modalProps["data-testid"]}-close-button`
                      : undefined
                  }
                  onClick={() => {
                    onCloseClick();
                  }}
                >
                  <CloseIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </button>
                {children}
                {otpModal ? (
                  <></>
                ) : (
                  <>
                    {!signModal ? (
                      <>
                        <div
                          className={`${
                            signModal ? "pt-5" : "lg:pt-[30px] pt-5"
                          } w-full flex lg:justify-end lg:gap-5 gap-[10px]`}
                        >
                          <Button
                            text={cancleText}
                            variant="stroke-outline"
                            onClick={onCloseClick}
                            className={"lg:w-fit w-1/2"}
                            isDisabled={isLoading}
                            data-testid={
                              modalProps["data-testid"]
                                ? `${modalProps["data-testid"]}-cancel-button`
                                : undefined
                            }
                            aria-label={cancleText}
                          />
                          {hasDoneButton ? (
                            <Button
                              text={doneText}
                              onClick={onDoneClick}
                              isLoading={isLoading}
                              className={"lg:w-fit w-1/2"}
                              data-testid={
                                modalProps["data-testid"]
                                  ? `${modalProps["data-testid"]}-confirm-button`
                                  : undefined
                              }
                              aria-label={doneText}
                            />
                          ) : null}
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CustomModal;
